import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { FolderTree, Gift, LayoutGrid, ListTree, Tag } from "lucide-react";
import DonorTypePage from "./DonorType";
import DonorCategoryPage from "./DonorCategory";
import DonationTypePage from "./DonationType";
import DonationMainCategoryPage from "./DonationMainCategory";
import DonationSubCategoryPage from "./DonationSubCategory";

const TAB_VALUES = [
  "donor-type",
  "donor-category",
  "donation-type",
  "donation-main-category",
  "donation-sub-category",
] as const;

type DonationMasterTab = (typeof TAB_VALUES)[number];

const isValidTab = (value: string | null): value is DonationMasterTab =>
  TAB_VALUES.includes(value as DonationMasterTab);

const DONATION_MASTER_TABS: {
  value: DonationMasterTab;
  label: string;
  icon: typeof Tag;
  Panel: typeof DonorTypePage;
}[] = [
  { value: "donor-type", label: "Donor Type", icon: Tag, Panel: DonorTypePage },
  {
    value: "donor-category",
    label: "Donor Category",
    icon: FolderTree,
    Panel: DonorCategoryPage,
  },
  {
    value: "donation-type",
    label: "Donation Type",
    icon: Gift,
    Panel: DonationTypePage,
  },
  {
    value: "donation-main-category",
    label: "Donation Main Category",
    icon: LayoutGrid,
    Panel: DonationMainCategoryPage,
  },
  {
    value: "donation-sub-category",
    label: "Donation Sub Category",
    icon: ListTree,
    Panel: DonationSubCategoryPage,
  },
];

export default function DonationMastersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: DonationMasterTab = isValidTab(tabParam)
    ? tabParam
    : "donor-type";

  useEffect(() => {
    if (!isValidTab(tabParam)) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [tabParam, activeTab, setSearchParams]);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Donation Masters</h1>
        <p className="text-muted-foreground">
          Manage donor types, donor categories, donation types, main categories,
          and sub categories in one place
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={value => setSearchParams({ tab: value })}
        className="space-y-4"
      >
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-3 lg:max-w-5xl lg:grid-cols-5">
          {DONATION_MASTER_TABS.map(({ value, label, icon: Icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="gap-2 px-3 py-2.5 text-xs sm:text-sm"
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {DONATION_MASTER_TABS.map(({ value, Panel }) => (
          <TabsContent key={value} value={value} className="mt-0 space-y-4">
            {activeTab === value && <Panel embedded />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
